// Flag to prevent concurrent free gift processing
let isProcessingFreeGifts = false;
// Debounce timer for renderGWPItems
let renderGWPItemsTimeout = null;

class CartGWP extends HTMLElement {
	constructor() {
		super();
		this.cartUpdateUnsubscriber = undefined;
		this._isRendering = false;
	}

	connectedCallback() {
		this.renderGWPItems();
		// this.subscribeToCartUpdates();
		// checkAndAddFreeGiftProducts();
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	subscribeToCartUpdates() {
		if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
				// Skip if the update is from free-gift operations to prevent loops
				if (event.source === 'free-gift' ||
					event.source === 'free-gift-removal' ||
					event.source === 'free-gift-quantity-update') {
					return;
				}
				// Debounce to prevent rapid-fire updates
				clearTimeout(this.cartUpdateTimeout);
				this.cartUpdateTimeout = setTimeout(() => {
					this.handleCartUpdate();
				}, 500);
			});
		}
	}

	async renderGWPItems() {
		// Prevent concurrent renders
		if (this._isRendering) {
			return;
		}

		// Skip if free gifts are being processed
		if (isProcessingFreeGifts) {
			return;
		}

		if (typeof freeGiftConfig === 'undefined' || !Array.isArray(freeGiftConfig) || freeGiftConfig.length === 0) {
			return;
		}

		this._isRendering = true;

		try {
			// Don't check/remove free gifts here - that should be done separately
			// Just render what's currently in the cart
			const cartData = await this.getCartData();
			const cartTotal = cartData.total_price || 0;

			// Normalize items
			const normalizedItems = (cartData.items || []).map(item => item.value || item);

			const freeGiftVariantIdsInCart = new Set(
				normalizedItems
					.filter(item => item.properties && (item.properties['Free Gift'] === 'yes' || item.properties['Free Item'] === 'yes'))
					.map(item => item.variant_id)
			);

			const freeGiftsToRender = freeGiftConfig.filter(config => {
				// Return configs where freeGiftVariantId exists in freeGiftVariantIdsInCart
				return config.freeGiftVariantId && freeGiftVariantIdsInCart.has(config.freeGiftVariantId);
			});

			// Only show GWP section if there are free gifts in cart
			if (freeGiftsToRender.length === 0) {
				this.innerHTML = '';
				return;
			}

			this.innerHTML = '';

			const wrapper = document.createElement('div');
			wrapper.className = 'cart-drawer__gwp-wrapper';

			// Use the eligible configs returned from checkIfEligibleForFreeGift
			for (const config of freeGiftsToRender) {
				const thresholdMet = cartTotal >= config.priceThreshold;
				const gwpItem = this.createGWPItem(config);
				wrapper.appendChild(gwpItem);
			}

			if (wrapper.children.length > 0) {
				this.appendChild(wrapper);
			}
		} finally {
			this._isRendering = false;
		}
	}

	// Debounced version for external calls
	renderGWPItemsDebounced() {
		clearTimeout(renderGWPItemsTimeout);
		renderGWPItemsTimeout = setTimeout(() => {
			this.renderGWPItems();
		}, 300);
	}

	/**
	 * Check eligibility and add free gifts to cart
	 * This should only be called when a product is added to cart, not on page load
	 */
	async checkAndAddFreeGifts() {
		if (isProcessingFreeGifts) {
			console.log('[Cart GWP] Already processing free gifts, skipping checkAndAddFreeGifts...');
			return;
		}

		if (typeof freeGiftConfig === 'undefined' || !Array.isArray(freeGiftConfig) || freeGiftConfig.length === 0) {
			return;
		}

		isProcessingFreeGifts = true;

		try {
			// First, check if cart only has free gifts and remove them if so
			const wasCleared = await this.removeFreeGiftsIfOnlyFreeGiftsInCart();
			if (wasCleared) {
				// Cart was cleared, no need to add free gifts
				return;
			}

			// Fetch cart data once and reuse it
			const cartData = await this.getCartData();
			const freeGiftItemsToAddToCart = this.checkIfEligibleForFreeGift(cartData);

			if (freeGiftItemsToAddToCart.length > 0) {
				for (const config of freeGiftItemsToAddToCart) {
					await addFreeGiftToCart(config);
				}
				// Re-render after adding (debounced)
				this.renderGWPItemsDebounced();
			}
		} finally {
			isProcessingFreeGifts = false;
		}
	}

	/**
	 * Remove all free gift items from cart if cart only contains free gifts
	 * Clears the entire cart instead of removing items individually
	 * @returns {boolean} true if cart was cleared, false otherwise
	 */
	async removeFreeGiftsIfOnlyFreeGiftsInCart() {
		try {
			const cartData = await this.getCartData();
			const items = cartData.items || [];

			if (items.length === 0) {
				return false;
			}

			// Normalize items (unwrap from 'value' key if present)
			const normalizedItems = items.map(item => item.value || item);

			// Check if all items are free gifts
			const allItemsAreFreeGifts = normalizedItems.every(item => {
				return item.properties && (item.properties['Free Gift'] === 'yes' || item.properties['Free Item'] === 'yes');
			});

			if (allItemsAreFreeGifts) {
				console.log('[Cart GWP] Cart only contains free gifts, clearing entire cart');
				// Clear the entire cart instead of removing items individually
				await clearCart();
				return true; // Cart was cleared
			}
			return false; // Cart was not cleared
		} catch (error) {
			console.error('[Cart GWP] Error checking and removing free gifts:', error);
			return false;
		}
	}

	/**
	 * Check and remove free gifts when cart total drops below threshold
	 * or when remaining products don't belong to the free gift's collection
	 * This should be called after products are removed or quantities are changed
	 */
	async checkAndRemoveFreeGifts() {
		if (isProcessingFreeGifts) {
			console.log('[Cart GWP] Already processing free gifts, skipping checkAndRemoveFreeGifts...');
			return;
		}

		if (typeof freeGiftConfig === 'undefined' || !Array.isArray(freeGiftConfig) || freeGiftConfig.length === 0) {
			return;
		}

	//	isProcessingFreeGifts = true;

		try {
			// First check if cart only has free gifts (this will clear the cart if so)
			const wasCleared = await this.removeFreeGiftsIfOnlyFreeGiftsInCart();
			if (wasCleared) {
				// Cart was cleared, no need to check individual free gifts
				return;
			}

			const cartData = await this.getCartData();
			if (!cartData || !cartData.items || cartData.items.length === 0) {
				return;
			}

			// Normalize all items first (unwrap from 'value' key if present)
			const normalizedAllItems = cartData.items.map(item => item.value || item);

			// Separate regular items and free gift items
			const regularItems = normalizedAllItems.filter(item => {
				return !item.properties ||
					(item.properties['Free Gift'] !== 'yes' && item.properties['Free Item'] !== 'yes');
			});

			const freeGiftItems = normalizedAllItems.filter(item => {
				return item.properties &&
					(item.properties['Free Gift'] === 'yes' || item.properties['Free Item'] === 'yes');
			});

			// Calculate regular items total (exclude free gifts from total)
			const regularItemsTotal = regularItems.reduce((sum, item) => {
				return sum + (item.price * item.quantity);
			}, 0);

			console.log('[Cart GWP] Checking free gifts for removal:', {
				regularItemsCount: regularItems.length,
				regularItemsTotal,
				freeGiftItemsCount: freeGiftItems.length
			});

			// Check each free gift item in cart
			for (const freeGiftItem of freeGiftItems) {
				// Verify this is actually a free gift
				const isFreeGift = freeGiftItem.properties &&
					(freeGiftItem.properties['Free Gift'] === 'yes' || freeGiftItem.properties['Free Item'] === 'yes');

				if (!isFreeGift) {
					continue;
				}

				const freeGiftVariantId = freeGiftItem.variant_id;

				// Find matching config for this free gift
				const matchingConfig = freeGiftConfig.find(config => config.freeGiftVariantId === freeGiftVariantId);

				if (matchingConfig) {
					let shouldRemove = false;
					let removalReason = '';

					// Check 1: Is cart total below the threshold?
					if (regularItemsTotal < matchingConfig.priceThreshold) {
						shouldRemove = true;
						removalReason = 'cart total below threshold';
					} else {
						// Check 2: Do any remaining regular items belong to the free gift's collection?
						const hasMatchingCollection = regularItems.some(item => {
							if (!item.collections || !Array.isArray(item.collections)) {
								return false;
							}
							// Check if any of the regular item's collections match the free gift's gwpCollection
							return item.collections.includes(matchingConfig.gwpCollection);
						});

						if (!hasMatchingCollection) {
							shouldRemove = true;
							removalReason = 'no products match free gift collection';
						}
					}

					if (shouldRemove) {
						console.log('[Cart GWP] Removing free gift -', removalReason, {
							regularItemsTotal,
							threshold: matchingConfig.priceThreshold,
							gwpCollection: matchingConfig.gwpCollection,
							freeGiftTitle: freeGiftItem.title,
							freeGiftVariantId,
							freeGiftKey: freeGiftItem.key
						});

						// Verify one more time this is a free gift before removing
						if (freeGiftItem.key && isFreeGift) {
							await removeFreeGiftFromCart(freeGiftItem.key);
							// After removing, re-fetch cart and continue checking
							const updatedCartData = await this.getCartData();
							return this.checkAndRemoveFreeGifts();
						}
					}
				}
			}
		} catch (error) {
			console.error('[Cart GWP] Error checking and removing free gifts:', error);
		}
	}

	checkIfEligibleForFreeGift(cartData) {
		const cartTotal = cartData.total_price || 0;
		const items = cartData.items || [];
		const freeGiftItemsToRender = [];
		const addedConfigIds = new Set(); // Track configs already added to avoid duplicates

		// Normalize all items first (unwrap from 'value' key if present)
		const normalizedAllItems = items.map(item => item.value || item);

		const freeGiftVariantIdsInCart = new Set(
			normalizedAllItems
				.filter(item => item.properties && (item.properties['Free Gift'] === 'yes' || item.properties['Free Item'] === 'yes'))
				.map(item => item.variant_id)
		);

		if (!normalizedAllItems || normalizedAllItems.length === 0) {
			return [];
		}

		// Iterate through items to find matching configs
		for (const item of normalizedAllItems) {
			// Skip free gift items themselves
			if (item.properties && (item.properties['Free Gift'] === 'yes' || item.properties['Free Item'] === 'yes')) {
				continue;
			}

			// Check if item has collections
			if (!item.collections || !Array.isArray(item.collections) || item.collections.length === 0) {
				continue;
			}

			// Check each freeGiftConfig to see if this item matches
			for (const config of freeGiftConfig) {
				// Check if cart total meets the price threshold
				if (cartTotal < config.priceThreshold) {
					continue;
				}

				// Check if item's collections include the gwpCollection
				const hasMatchingCollection = item.collections.some(collection => collection === config.gwpCollection);

				if (hasMatchingCollection) {
					// Use a unique identifier to avoid duplicates (e.g., priceThreshold + gwpCollection)
					const configKey = `${config.priceThreshold}-${config.gwpCollection}`;
					console.log('configKey', configKey);
					if (!addedConfigIds.has(configKey) && !freeGiftVariantIdsInCart.has(config.freeGiftVariantId)) {
						freeGiftItemsToRender.push(config);
						addedConfigIds.add(configKey);
					}
				}
			}
		}

		return freeGiftItemsToRender;
	}

	createGWPItem(item) {
		const gwpItem = document.createElement('div');
		gwpItem.className = 'gwp-block__gwp-item';

		const imageWrapper = document.createElement('div');
		imageWrapper.className = 'gwp-image-wrapper';

		let imageSrc = '';
		let imageAlt = item.freeGiftTitle || 'Free Gift';

		if (item.freeGiftProduct) {
			try {
				const product = typeof item.freeGiftProduct === 'string'
					? JSON.parse(item.freeGiftProduct)
					: item.freeGiftProduct;

				if (product.featured_image) {
					imageSrc = product.featured_image;
				} else if (product.featured_media) {
					if (typeof product.featured_media === 'string') {
						imageSrc = product.featured_media;
					} else if (product.featured_media.src) {
						imageSrc = product.featured_media.src;
					} else if (product.featured_media.preview_image) {
						imageSrc = product.featured_media.preview_image.src;
					}
				} else if (product.images && product.images.length > 0) {
					imageSrc = product.images[0];
				}

				if (product.title) {
					imageAlt = product.title;
				}
			} catch (e) {
				console.warn('Error parsing freeGiftProduct:', e);
			}
		}

		if (!imageSrc) {
			imageSrc = '/assets/gwp-image.png';
		}

		const img = document.createElement('img');
		img.src = imageSrc;
		img.alt = imageAlt;
		img.width = 99;
		img.height = 72;
		img.loading = 'lazy';

		imageWrapper.appendChild(img);

		const details = document.createElement('div');
		details.className = 'gwp-details';

		const title = document.createElement('h3');
		title.textContent = item.freeGiftTitle || 'Free Gift with purchase';

		const description = document.createElement('p');
		description.textContent = item.freeGiftDesc || '';

		details.appendChild(title);
		details.appendChild(description);

		gwpItem.appendChild(imageWrapper);
		gwpItem.appendChild(details);

		return gwpItem;
	}

	async getCartData() {
		try {
			const response = await fetch('/cart?view=data-json');
			// console.log('[CartGWP] Cart data response:', response);
			const cart = await response.json();
			return cart;
		} catch (error) {
			console.error('Error fetching cart data:', error);
			return { total_price: 0, items: [] };
		}
	}
}

// Function to clear the entire cart
async function clearCart() {
	try {
		// Get cart drawer/page element to fetch sections
		const cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');
		if (!cart) {
			console.warn('[Cart GWP] No cart element found');
			return;
		}

		// Get sections to render
		const sectionsToRender = cart.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.section) : ['cart-drawer', 'cart-icon-bubble'];
		const sectionsUrl = window.location.pathname;

		// Remove items one by one (more reliable than bulk update)
		// Keep fetching fresh cart state as we remove items
		let cartItems = [];
		do {
			const cartResponse = await fetch('/cart.js');
			const cartData = await cartResponse.json();
			cartItems = cartData.items || [];

			if (cartItems.length === 0) {
				break;
			}

			// Remove the first item (line 1)
			const body = JSON.stringify({
				line: 1,
				quantity: 0,
				sections: sectionsToRender,
				sections_url: sectionsUrl
			});

			const config = fetchConfig('javascript');
			config.body = body;

			const response = await fetch(`${routes.cart_change_url}`, config);
			const responseText = await response.text();
			const parsedState = JSON.parse(responseText);
          
			if (window.location.pathname === '/cart') {
				location.reload();
			}
			// If this was the last item, update the frontend
			if (parsedState.item_count === 0 || cartItems.length === 1) {
				// Fetch fresh cart state with sections for proper UI update
				try {
					const finalCartResponse = await fetch('/cart.js');
					const finalCartData = await finalCartResponse.json();

					// Fetch sections if needed
					if (cart.getSectionsToRender && (!parsedState.sections || Object.keys(parsedState.sections).length === 0)) {
						const sectionsToFetch = cart.getSectionsToRender().map((section) => section.id);
						const sectionsUrl = window.location.pathname;

						// Fetch each section
						const sections = {};
						for (const sectionId of sectionsToFetch) {
							try {
								const sectionResponse = await fetch(`/?section_id=${sectionId}`);
								const sectionHtml = await sectionResponse.text();
								sections[sectionId] = sectionHtml;
							} catch (e) {
								console.warn(`[Cart GWP] Failed to fetch section ${sectionId}:`, e);
							}
						}

						parsedState.sections = sections;
					}

					// Update empty state classes
					cart.classList.add('is-empty');
					const cartDrawerWrapper = document.querySelector('cart-drawer');
					const cartFooter = document.getElementById('main-cart-footer');
					if (cartFooter) cartFooter.classList.add('is-empty');
					if (cartDrawerWrapper) cartDrawerWrapper.classList.add('is-empty');

					// Update the frontend if sections are available
					if (cart.renderContents && parsedState.sections && Object.keys(parsedState.sections).length > 0) {
						cart.renderContents(parsedState);
					}
				} catch (fetchError) {
					console.warn('[Cart GWP] Error fetching final cart state:', fetchError);
					// Still update empty state classes
					cart.classList.add('is-empty');
					const cartDrawerWrapper = document.querySelector('cart-drawer');
					const cartFooter = document.getElementById('main-cart-footer');
					if (cartFooter) cartFooter.classList.add('is-empty');
					if (cartDrawerWrapper) cartDrawerWrapper.classList.add('is-empty');
				}

				// Publish cart update event (this will trigger other components to update)
				if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
					publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-clear', cartData: parsedState });
				}

				break;
			}

			// Small delay to avoid rate limiting
			await new Promise(resolve => setTimeout(resolve, 100));
		} while (cartItems.length > 0);

		console.log('[Cart GWP] Cart cleared successfully and frontend updated');
	} catch (error) {
		console.error('[Cart GWP] Error clearing cart:', error);
	}
}

// Function to remove a free gift from cart and update frontend
async function removeFreeGiftFromCart(itemKey) {
	try {
		// Get cart drawer/page element to fetch sections
		const cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');
		if (!cart) {
			console.warn('[Cart GWP] No cart element found');
			return;
		}

		const sectionsToRender = cart.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.section) : ['cart-drawer', 'cart-icon-bubble'];
		const sectionsUrl = window.location.pathname;

		const cartResponse = await fetch('/cart?view=data-json');
		const cartData = await cartResponse.json();
		const cartItems = cartData.items || [];

		// Normalize items and find the item by key
		const normalizedItems = cartItems.map(item => item.value || item);
		const itemToRemove = normalizedItems.find(item => item.key === itemKey);

		if (!itemToRemove) {
			console.warn('[Cart GWP] Item not found by key:', itemKey);
			return;
		}

		// Safety check: Verify this is actually a free gift item
		const isFreeGift = itemToRemove.properties &&
			(itemToRemove.properties['Free Gift'] === 'yes' || itemToRemove.properties['Free Item'] === 'yes');

		if (!isFreeGift) {
			console.error('[Cart GWP] SAFETY CHECK FAILED - Attempted to remove regular product as free gift:', {
				title: itemToRemove.title,
				key: itemKey,
				properties: itemToRemove.properties
			});
			return; // Do not remove regular products
		}

		// Find the index in the original cartItems array (before normalization)
		const itemIndex = cartItems.findIndex(item => {
			const normalized = item.value || item;
			return normalized.key === itemKey;
		});

		if (itemIndex === -1) {
			console.warn('[Cart GWP] Item index not found:', itemKey);
			return;
		}

		const line = itemIndex + 1;
		const body = JSON.stringify({
			line,
			quantity: 0,
			sections: sectionsToRender,
			sections_url: sectionsUrl
		});

		const config = fetchConfig('javascript');
		config.body = body;

		const response = await fetch(`${routes.cart_change_url}`, config);
		const responseText = await response.text();
		const parsedState = JSON.parse(responseText);

		if (parsedState.errors) {
			throw new Error(`Cart update failed: ${parsedState.errors}`);
		}

		// Fetch sections if they're missing from the response
		if (!parsedState.sections || Object.keys(parsedState.sections).length === 0) {
			if (cart.getSectionsToRender) {
				const sectionsToFetch = cart.getSectionsToRender().map((section) => section.id);
				const sections = {};
				for (const sectionId of sectionsToFetch) {
					try {
						const sectionResponse = await fetch(`/?section_id=${sectionId}`);
						const sectionHtml = await sectionResponse.text();
						sections[sectionId] = sectionHtml;
					} catch (e) {
						console.warn(`[Cart GWP] Failed to fetch section ${sectionId}:`, e);
					}
				}
				parsedState.sections = sections;
			}
		}

		// Update the frontend - use the same approach as cart-drawer.js renderContents
		if (parsedState.sections && cart.getSectionsToRender) {
			cart.getSectionsToRender().forEach((section) => {
				// cart-drawer.js uses section.id as the key, not section.section
				const sectionHtml = parsedState.sections[section.id];

				if (!sectionHtml) {
					console.warn(`[Cart GWP] Section ${section.id} not found in response`);
					return;
				}

				const sectionElement = section.selector
					? document.querySelector(section.selector)
					: document.getElementById(section.id);

				if (!sectionElement) {
					console.warn(`[Cart GWP] Section element not found: ${section.id}`);
					return;
				}

				try {
					// Use cart's getSectionInnerHTML method if available, otherwise parse manually
					if (cart.getSectionInnerHTML) {
						try {
							const innerHTML = cart.getSectionInnerHTML(sectionHtml, section.selector);
							if (innerHTML) {
								sectionElement.innerHTML = innerHTML;
							}
						} catch (innerError) {
							// If getSectionInnerHTML fails (e.g., selector not found), try fallback
							console.warn(`[Cart GWP] getSectionInnerHTML failed for ${section.id}, using fallback:`, innerError);
							const parser = new DOMParser();
							const doc = parser.parseFromString(sectionHtml, 'text/html');
							const selector = section.selector || '.shopify-section';
							const parsedElement = doc.querySelector(selector);
							if (parsedElement) {
								sectionElement.innerHTML = parsedElement.innerHTML;
							}
						}
					} else {
						// Fallback: parse HTML manually
						const parser = new DOMParser();
						const doc = parser.parseFromString(sectionHtml, 'text/html');
						const selector = section.selector || '.shopify-section';
						const parsedElement = doc.querySelector(selector);
						if (parsedElement) {
							sectionElement.innerHTML = parsedElement.innerHTML;
						}
					}
				} catch (e) {
					console.warn(`[Cart GWP] Error updating section ${section.id}:`, e);
				}
			});
		}

		// Update empty state classes
		cart.classList.toggle('is-empty', parsedState.item_count === 0);
		const cartDrawerWrapper = document.querySelector('cart-drawer');
		const cartFooter = document.getElementById('main-cart-footer');
		if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
		if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

		// Reload cart page if cart is empty and we're on the /cart page
		if (parsedState.item_count === 0 && window.location.pathname === '/cart') {
			console.log('[Cart GWP] Cart is empty on /cart page, reloading to show empty state');
			window.location.reload();
			return;
		}

		// Publish cart update event
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift-removal', cartData: parsedState });
		}

		console.log('[Cart GWP] Free gift removed and frontend updated:', itemToRemove.title);
	} catch (error) {
		console.error('[Cart GWP] Error removing free gift from cart:', error);
	}
}

// Function to add a free gift product to cart and update frontend
async function addFreeGiftToCart(item) {
	if (!item.freeGiftVariantId) {
		return false;
	}

	try {
		const variantId = item.freeGiftVariantId;

		if (!variantId) {
			return false;
		}

		// Get cart drawer/page element to fetch sections
		const cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');
		const sectionsToRender = cart && cart.getSectionsToRender ? cart.getSectionsToRender().map((section) => section.id) : ['cart-drawer', 'cart-icon-bubble'];
		const sectionsUrl = window.location.pathname;

		const formData = new FormData();
		formData.append('id', variantId);
		formData.append('quantity', 1);
		formData.append('properties[Free Gift]', 'yes');
		if (cart) {
			formData.append('sections', sectionsToRender.join(','));
			formData.append('sections_url', sectionsUrl);
		}

		const config = fetchConfig('javascript');
		config.headers['X-Requested-With'] = 'XMLHttpRequest';
		delete config.headers['Content-Type'];
		config.body = formData;

		const response = await fetch(`${routes.cart_add_url}`, config);
		const result = await response.json();

		if (result.status) {
			console.error('[Cart GWP] Error adding free gift to cart:', result.description);
			return false;
		}

		// Update the frontend
		if (cart && cart.renderContents) {
			cart.renderContents(result);
		}

		// Publish cart update event
		if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
			publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: result });
		}

		// Note: renderGWPItems will be called by checkAndAddFreeGifts after all gifts are added
		console.log('[Cart GWP] Free gift added and frontend updated');
		return true;
	} catch (error) {
		console.error('[Cart GWP] Error adding free gift to cart:', error);
		return false;
	}
}

/**
 * Automatically add free gift products when eligible products are added to cart
 * Listens to cartUpdate events and checks if newly added products match freeGiftConfig
 */
async function addRemoveFreeGiftBasedOnEligibility(event) {
	console.log('[Cart GWP] addRemoveFreeGiftBasedOnEligibility - event:', event);
	// Check if freeGiftConfig is available
	if (typeof freeGiftConfig === 'undefined' || !Array.isArray(freeGiftConfig) || freeGiftConfig.length === 0) {
		console.log('[Cart GWP] freeGiftConfig not available');
		return;
	}

	try {
		// Fetch cart data from /cart?view=data-json (same as renderGWPItems uses)
		const cartDataResponse = await fetch('/cart?view=data-json');
		const cartData = await cartDataResponse.json();
		console.log('[Cart GWP] Cart data:', cartData);

		const cartTotal = cartData.total_price || 0;
		const items = cartData.items || [];

		// Get existing free gift variant IDs from cart using /cart?view=data-json
		// Normalize items (unwrap from 'value' key if present)
		const freeGiftVariantIdsInCart = new Set(
			items
				? items
						.filter(item => {
							const normalizedItem = item.value || item;
							return normalizedItem.properties && (normalizedItem.properties['Free Gift'] === 'yes' || normalizedItem.properties['Free Item'] === 'yes');
						})
						.map(item => {
							const normalizedItem = item.value || item;
							return normalizedItem.variant_id;
						})
				: []
		);
		console.log('[Cart GWP] Existing free gift variant IDs in cart:', Array.from(freeGiftVariantIdsInCart));

		if (!items || items.length === 0) {
			console.log('[Cart GWP] No items in cart');
			return;
		}

		// Use the same logic as checkIfEligibleForFreeGift (from renderGWPItems)
		const freeGiftItemsToAdd = [];
		const addedConfigIds = new Set();

		// Iterate through items to find matching configs
		for (const item of items) {
			// Normalize item (unwrap from 'value' key if present)
			const normalizedItem = item.value || item;

			// Skip free gift items themselves
			if (normalizedItem.properties && (normalizedItem.properties['Free Gift'] === 'yes' || normalizedItem.properties['Free Item'] === 'yes')) {
				continue;
			}

			// Check if item has collections
			if (!normalizedItem.collections || !Array.isArray(normalizedItem.collections) || normalizedItem.collections.length === 0) {
				continue;
			}

			// Check each freeGiftConfig to see if this item matches
			for (const config of freeGiftConfig) {
				// Check if cart total meets the price threshold
				if (cartTotal < config.priceThreshold) {
					continue;
				}

				// Check if item's collections include the gwpCollection
				const hasMatchingCollection = normalizedItem.collections.some(collection => collection === config.gwpCollection);

				if (hasMatchingCollection) {
					// Use a unique identifier to avoid duplicates
					const configKey = `${config.priceThreshold}-${config.gwpCollection}`;

					// Check if this free gift variant ID already exists in cart (prevent duplicates)
					if (!addedConfigIds.has(configKey) && !freeGiftVariantIdsInCart.has(config.freeGiftVariantId)) {
						freeGiftItemsToAdd.push(config);
						addedConfigIds.add(configKey);
						console.log('[Cart GWP] ✅ Eligible config found:', config);
						console.log('[Cart GWP] Config key:', configKey);
						console.log('[Cart GWP] Free gift variant ID:', config.freeGiftVariantId);
					} else {
						console.log('[Cart GWP] Config already added or free gift already in cart:', configKey, 'variant ID:', config.freeGiftVariantId);
					}
				}
			}
		}

		// Add eligible free gifts to cart
		if (freeGiftItemsToAdd.length > 0) {
			console.log('[Cart GWP] Adding free gift items:', freeGiftItemsToAdd);
			for (const config of freeGiftItemsToAdd) {
				await addFreeGiftToCart(config);
			}
		} else {
			console.log('[Cart GWP] No eligible free gift items to add');
		}
	} catch (error) {
		console.error('[Cart GWP] Error checking and adding free gift:', error);
	}
}

// Note: Free gift add logic is now handled directly in product-form.js after product submission
// The addRemoveFreeGiftBasedOnEligibility function is kept for potential future use but not subscribed

if (!customElements.get('cart-gwp')) {
	customElements.define('cart-gwp', CartGWP);
}

